import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";

const TermsOfService = () => (
  <div className="min-h-screen bg-background">
    <SEO
      title="Terms of Service | HeirWise"
      description="The terms that govern your use of HeirWise's website, platform, and educational services."
      path="/terms-of-service"
    />
    <div className="container mx-auto max-w-3xl px-6 py-12 sm:py-16">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8">
        <ArrowLeft className="h-4 w-4" /> Back to Home
      </Link>

      <h1 className="text-3xl sm:text-4xl font-serif font-bold text-foreground mb-2">Terms of Service</h1>
      <p className="text-sm text-muted-foreground mb-10">Last Updated: September 23, 2026</p>

      <div className="prose prose-sm sm:prose-base max-w-none text-foreground/90 [&_h2]:font-serif [&_h2]:text-foreground [&_h2]:text-xl [&_h2]:sm:text-2xl [&_h2]:mt-10 [&_h2]:mb-4 [&_p]:leading-relaxed [&_p]:mb-4 [&_ul]:mb-4 [&_li]:mb-1.5">

        <p>
          These Terms of Service ("Terms") govern your access to and use of the HeirWise website at heirwise.app, platform, educational content, assessments, simulations, progress features, subscriptions, and related services operated by HeirWise LLC (collectively, the "Services"). By accessing or using the Services, you agree to these Terms. If you do not agree, do not use the Services.
        </p>

        <h2>1. Agreement to These Terms</h2>
        <p>
          By accessing or using the Services, you agree to be bound by these Terms and our{" "}
          <Link to="/privacy-policy" className="text-primary underline underline-offset-2">Privacy Policy</Link>. If you do not agree, you may not access or use the Services.
        </p>

        <h2>2. About the Services</h2>
        <p>
          HeirWise LLC operates a paid Economic & Enterprise Education platform for practical decision-making across finance, economics, entrepreneurship, investing, business, banking, credit, budgeting, real estate, and related subjects. The Services may include lessons, assessments, decision practice, simulations, adaptive learning features, progress reporting, account features, subscriptions, and support tools. Curriculum certification does not mean that every certified lesson is currently released or available.
        </p>

        <h2>3. Eligibility</h2>
        <p>
          The Services are intended only for users who are 13 years of age or older. Children under 13 are not permitted to create accounts or use the Services in a manner that requires submission of personal information. By using the Services, you represent and warrant that you are at least 13 years old and otherwise legally able to enter into these Terms.
        </p>
        <p>
          If you are under the age of majority in your state of residence but at least 13 years old, you may use the Services only with the involvement and supervision of a parent or legal guardian, to the extent required by applicable law.
        </p>
        <p>
          Parent or guardian account holders may create and manage a household and authorize learner access for eligible teens. The account holder is responsible for household invitations, learner profiles, and activity under the household account.
        </p>

        <h2>4. Accounts</h2>
        <p>
          You may be required to create an account to access certain features of the Services. You agree to provide accurate, current, and complete information and to keep it updated. You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account.
        </p>
        <p>
          We may suspend or terminate accounts that violate these Terms, create risk, or interfere with the Services or other users.
        </p>

        <h2>5. Acceptable Use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>use the Services for unlawful, fraudulent, abusive, harassing, or infringing purposes;</li>
          <li>copy, scrape, reverse engineer, disrupt, test vulnerabilities of, or interfere with the Services except as expressly permitted by law;</li>
          <li>upload malware, malicious code, or other harmful material;</li>
          <li>use bots, scripts, or automated methods in ways that overburden, bypass, or interfere with the intended operation of the Services;</li>
          <li>misrepresent your identity, age, affiliation, or authority; or</li>
          <li>use the Services in a manner that violates the rights of HeirWise LLC, other users, or third parties.</li>
        </ul>

        <h2>6. Content and Intellectual Property</h2>
        <p>
          The Services, including software, design, text, graphics, logos, videos, educational materials, simulations, workflows, and other content provided by us, are owned by HeirWise LLC or its licensors and are protected by applicable intellectual property laws.
        </p>
        <p>
          Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Services for their intended personal, non-commercial educational purpose.
        </p>

        <h2>7. User Content</h2>
        <p>
          If you submit content, feedback, notes, responses, uploads, or other materials through the Services ("User Content"), you represent that you have the rights needed to submit it. You grant us a non-exclusive, worldwide, royalty-free license to host, store, reproduce, process, and display User Content solely as necessary to operate, improve, secure, and support the Services.
        </p>
        <p>You remain responsible for the User Content you submit.</p>

        <h2>8. Paid Plans, Billing, and Cancellation</h2>
        <p>
          HeirWise requires a paid subscription for ordinary learner access. By purchasing a plan, you authorize us and our third-party payment processors to charge the fees, taxes, and recurring subscription charges disclosed to you at the time of purchase.
        </p>
        <ul>
          <li>HeirWise does not offer a free trial. Paid access begins through the applicable signup and checkout flow.</li>
          <li>Individual plans support a maximum of one teen. Family plans support a maximum of four teens.</li>
          <li>Current standard plans are Individual at $24.99 per month or $249 per year and Family at $39.99 per month or $399 per year. The pricing and checkout pages show the amount that applies when you purchase.</li>
          <li>Unless stated otherwise, subscriptions renew automatically until canceled.</li>
          <li>You may cancel future renewals through your account settings, if available, or by contacting us at{" "}
            <a href="mailto:founder@steenventures.com" className="text-primary underline underline-offset-2">founder@steenventures.com</a>.</li>
          <li>Amounts already paid are non-refundable except where required by law or expressly stated in a written refund policy.</li>
          <li>We may change pricing for future billing periods by providing notice where required.</li>
        </ul>

        <h2>9. Third-Party Services</h2>
        <p>
          The Services may integrate with or link to third-party tools, payment processors, analytics providers, hosting providers, APIs, or websites. We are not responsible for third-party services, content, or policies, and your use of third-party services may be governed by separate terms and privacy policies.
        </p>

        <h2>10. Disclaimers</h2>
        <p>
          The Services are provided on an "as is" and "as available" basis to the fullest extent permitted by law. We do not guarantee that the Services will be uninterrupted, secure, error-free, or that any content, insights, recommendations, educational materials, or simulations will always be complete, current, or suitable for your specific circumstances.
        </p>
        <ul>
          <li>The Services are educational software and are not a bank, lender, broker, investment adviser, financial institution, wealth manager, or financial-services provider.</li>
          <li>The Services are for general educational and informational purposes only and do not constitute individualized financial, investment, tax, legal, credit, banking, lending, or other professional advice.</li>
          <li>Learning progress, recommendations, quiz outcomes, and simulations are illustrative and educational in nature and are not guarantees of real-world results.</li>
          <li>Virtual balances, lesson rewards, scores, simulations, and educational scenarios are not bank deposits, securities, investment accounts, credit products, or real money.</li>
          <li>Access to or use of the Services by users under 13 is prohibited.</li>
        </ul>

        <h2>11. Limitation of Liability</h2>
        <p>
          To the fullest extent permitted by law, HeirWise LLC and its affiliates, officers, employees, contractors, licensors, and service providers will not be liable for any indirect, incidental, special, consequential, exemplary, or punitive damages, or for any loss of profits, revenue, goodwill, data, business opportunities, or anticipated savings arising out of or related to the Services or these Terms.
        </p>
        <p>
          To the fullest extent permitted by law, our aggregate liability for claims arising out of or relating to the Services or these Terms will not exceed the amount you paid us, if any, during the twelve months before the event giving rise to the claim, or one hundred U.S. dollars (US $100), whichever is greater.
        </p>

        <h2>12. Indemnification</h2>
        <p>
          You agree to defend, indemnify, and hold harmless HeirWise LLC and its affiliates, officers, employees, contractors, licensors, and service providers from and against claims, liabilities, damages, judgments, losses, costs, and expenses, including reasonable attorneys' fees, arising out of or related to your misuse of the Services, your User Content, or your violation of these Terms or applicable law.
        </p>

        <h2>13. Suspension and Termination</h2>
        <p>
          We may suspend or terminate access to the Services at any time, with or without notice, if we believe you violated these Terms, created risk, interfered with the Services, or if suspension is needed for security, maintenance, legal compliance, or operational reasons.
        </p>

        <h2>14. Changes to the Services or Terms</h2>
        <p>
          We may modify the Services and these Terms from time to time. Updated Terms will be posted with a revised effective date. Your continued use of the Services after the updated Terms take effect constitutes your acceptance of the revised Terms.
        </p>

        <h2>15. Governing Law and Disputes</h2>
        <p>
          These Terms are governed by the laws of the State of Texas, without regard to conflict-of-law principles. Any dispute arising out of or relating to these Terms or the Services will be resolved exclusively in the state or federal courts located in Texas, unless replaced by a valid arbitration provision adopted later.
        </p>

        <h2>16. Contact Information</h2>
        <p>
          HeirWise LLC<br />
          Email:{" "}
          <a href="mailto:founder@steenventures.com" className="text-primary underline underline-offset-2">founder@steenventures.com</a>
        </p>
      </div>
    </div>
  </div>
);

export default TermsOfService;
